import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getData } from '@yme/api';
import { ArrowForward } from '@yme/assets/icons';
import { GHOST_VARIANT, SIZE_36, TRAILING_ICON } from '@yme/atoms/button';
import { Col, Container, Row } from '@yme/atoms/grid';
import { CardSkeleton } from '@yme/common/components/skeletons';
import { useIsMount, useWindowSize, useWishlist } from '@yme/hooks/index';
import { ViewAllMiniButton } from '@yme/molecules/buttons';
import { getProductDetailsWithGroupingProducts } from '@yme/pdp-components/ProductCard';
import { getPDPLinks } from '@yme/selectors/common';
import { getDaysToShowNew, getIsInAuthor, getRTL } from '@yme/selectors/index';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { HeadingLevel2 } from '@yme/tokens/typography';
import { buildKey } from '@yme/utils/common';
import { getPdpLinkByCategory, getPLPLinkWithAccessoriesAndApparels, getProductURL, } from '@yme/utils/product';
import { IndentWrapper } from '../Separator';
import { PartsAccessoriesItem } from './PartsAccessoriesItem';
import { LIMIT_PARTS_ACCESSORIES } from './constants';
import { buildUrl, getMasterAccessoriesList, getPrioritizedAccessories } from './helpers';
import { getAccessories } from './selectors';
import { LinkStyled, PartsAccessoriesStyled, ViewAllBottomStyled } from './styled';
function PartsAccessories(props) {
    const { labels, hyperdriveEndpoint, isRemoveBottomIndent, viewAllLink = '' } = props;
    const productDetailsData = useSelector(getProductDetailsWithGroupingProducts);
    const { isLiked, onLike } = useWishlist();
    const finallyLink = getPLPLinkWithAccessoriesAndApparels(viewAllLink, productDetailsData?.key);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [partsAccessoriesData, setPartsAccessoriesData] = useState([]);
    const [accessories, setAccessories] = useState([]);
    const pdpLinks = useSelector(getPDPLinks);
    const isInAuthor = useSelector(getIsInAuthor);
    const accessoriesIds = useSelector(getAccessories);
    const isRightToLeft = useSelector(getRTL);
    const daysToShowNew = useSelector(getDaysToShowNew);
    const isMount = useIsMount();
    // data for cards for UI component
    const masterAccessories = useMemo(() => getMasterAccessoriesList(accessories), [accessories]);
    const { isMobileView } = useWindowSize();
    const prioritizedAccessories = useMemo(() => getPrioritizedAccessories(masterAccessories, LIMIT_PARTS_ACCESSORIES), [masterAccessories]);
    useEffect(() => {
        if (accessoriesIds.length && !isMount) {
            setIsLoading(true);
            getData(buildUrl(hyperdriveEndpoint, accessoriesIds)).then((result) => {
                setAccessories(result);
                setIsLoaded(false);
                setIsLoading(false);
            });
        }
    }, [accessoriesIds, hyperdriveEndpoint, isMount]);
    useEffect(() => {
        if (!accessoriesIds.length && accessories.length) {
            setAccessories([]);
            setIsLoaded(false);
        }
    }, [accessoriesIds, accessories]);
    useEffect(() => {
        const results = prioritizedAccessories &&
            prioritizedAccessories?.reduce((acc, item) => {
                const path = getPdpLinkByCategory(pdpLinks, item.categories);
                const itemNewUrl = getProductURL({
                    path,
                    isInAuthor,
                    slug: item.slug,
                    sku: item.sku,
                });
                const newItemData = item
                    ? {
                        ...item,
                        url: itemNewUrl,
                    }
                    : {};
                if (!acc) {
                    return [newItemData];
                }
                return [...acc, newItemData];
            }, []);
        if (!isLoaded && results) {
            setPartsAccessoriesData(results);
            setIsLoaded(true);
        }
    }, [
        isInAuthor,
        isLoaded,
        masterAccessories,
        partsAccessoriesData,
        pdpLinks,
        prioritizedAccessories,
    ]);
    if (!partsAccessoriesData.length && !isLoading) {
        return null;
    }
    const sortedPartsAccessoriesData = [...partsAccessoriesData].sort((a, b) => b.popularityIndex - a.popularityIndex);
    return (React.createElement(IndentWrapper, { isRemoveBottomIndent: isRemoveBottomIndent },
        React.createElement(PartsAccessoriesStyled, { isRightToLeft: isRightToLeft },
            React.createElement(Container, null,
                React.createElement(Row, { justifyContent: "center", alignItems: "center" },
                    React.createElement(Col, { md: 5, lg: 10 },
                        React.createElement(HeadingLevel2, null, labels.featuredAccessories_header_label)),
                    React.createElement(Col, { md: 3, lg: 2 }, !isMobileView && viewAllLink && (React.createElement(FlexWrapper, { justifyContent: "flex-end" },
                        React.createElement(ViewAllMiniButton, { isRightToLeft: isRightToLeft, viewAllLabel: labels.common_viewAll_link, linkTo: finallyLink, tabIndex: -1 }))))),
                React.createElement(Row, null, isLoading
                    ? [...Array(LIMIT_PARTS_ACCESSORIES).keys()].map((item) => (React.createElement(CardSkeleton, { key: item })))
                    : sortedPartsAccessoriesData.map((accessory, index) => {
                        const key = buildKey(accessory.name, `${accessory.popularityIndex}_${index}`);
                        return (React.createElement(PartsAccessoriesItem, { ...accessory, daysToShowNew: daysToShowNew, key: key, isLiked: isLiked(accessory.sku ?? ''), onLike: onLike(accessory.sku ?? '', accessory.url ?? '') }));
                    })),
                isMobileView && viewAllLink && (React.createElement(Row, { mt: "24px" },
                    React.createElement(Col, null,
                        React.createElement(LinkStyled, { to: finallyLink, tabIndex: -1 },
                            React.createElement(ViewAllBottomStyled, { buttonVariant: GHOST_VARIANT, hasIconButton: TRAILING_ICON, iconComponent: ArrowForward, isRightToLeft: isRightToLeft, label: labels.common_viewAll_link, size: SIZE_36 })))))))));
}
export default PartsAccessories;
PartsAccessories.displayName = 'PartsAccessories_AEMComponent';
